import { V1AssetPerformanceDateRange } from '@blk/portfolio-security-server/analytics/portfolio-analytics';
import * as dayjs from 'dayjs';
import * as get from 'lodash/get';

import { TabData } from '../models/tab-data';

export class SecurityDataUtil {

	/**
	 * PositionPerformanceDateRange provides the ACTUAL performance data. 
	 * - If it does not have interval within PositionPerformanceDateRange, then it means it uses all parametric.
	 * - If it has interval then we check if it has a startDate and endDate. 
	 * 		- If the date range is less than the number of years specified, then it means it has used part parametric performance data
	 * 			therefore we pass back the startDate to show when the actual data ended and the parametric data began.
	 * 		- If the date range is greater than the number of years specified, then this means actual performance has been used for the
	 * 			full number of years specified, therefore no need to show the startDate as no splicing was used for your given number of years.
	 *
	 * @static
	 * @param {number} years
	 * @param {PositionPerformanceDateRange} positionPerformanceDateRange
	 * @returns {string}
	 * @memberof DataLayerUtil
	 */
	public static getSecurityPerformanceStartDateForLessThanYears(years: number, positionPerformanceDateRange: V1AssetPerformanceDateRange): string {
		if (positionPerformanceDateRange?.interval?.startDate && positionPerformanceDateRange?.interval?.stopDate) {
			const securityDateRange = positionPerformanceDateRange.interval;
			const startDate = dayjs(securityDateRange.startDate);
			const stopDate = dayjs(securityDateRange.stopDate);

			const stopSubtractOneYear = stopDate.subtract(years, 'year');
			const result = stopSubtractOneYear.isBefore(startDate);

			// Return the startDate if we have perf splicing otherwise if all years 
			// are ACTUAL performance, then no need to provide the startDate.
			return result ? get(securityDateRange, 'startDate') : null;
		}
		// If it doesnt have any dates, it means the whole security perf is using parametric
		return 'ALL_PARAMETRIC';
	}

	public static populateProductIdPerTickerMap(tabData: TabData): Map<string, number> {
		return SecurityDataUtil.populatePerSecurityIdMap(tabData, 'portfolioId');
	}

	private static populatePerSecurityIdMap(tabData: TabData, attributeToPopulate: string, tickerAsKey: boolean = true): Map<string, any> {
		const attributeSecurityIdMap = new Map();
		const attributeColIndex = get(tabData, 'tableData[0].cells[0]').findIndex(column => column.config.id === attributeToPopulate);
		get(tabData, 'tableData[0].cells').forEach((row, rowIndex) => {
			if (tickerAsKey) {
				attributeSecurityIdMap.set(get(tabData, `tableData[0].headersY[${rowIndex}].formattedValue`), row[attributeColIndex].formattedValue);
			} else {
				attributeSecurityIdMap.set(row[attributeColIndex].formattedValue, get(tabData, `tableData[0].headersY[${rowIndex}].formattedValue`));
			}
		});
		return attributeSecurityIdMap;
	}
}
